
import Vue from "vue";
import { Component, Prop, Inject } from "vue-property-decorator";
import { shouldShowControl } from "@/lib/controls/should-show-control";
import Control_ from "./Control.vue";
import { ControlsTreeContext, Control } from "@/types/controls";
import Accordion from "@/components/UI/Accordion.vue";
import Popout from "@/components/UI/Popout.vue";
import { clear, valuePresent } from "@/lib/controls/values";
import { uiStore } from "@/store/store-accessor";
import IndicatorLight from "./IndicatorLight.vue";
import Modal from "@/components/UI/Modal.vue";

@Component({
  name: "ControlsTreeExpansionPanels",
  components: {
    Modal,
    Control: Control_,
    Accordion,
    Popout,
    IndicatorLight
  }
})
export default class ControlsTreeExpansionPanels extends Vue {
  @Prop()
  controls!: Control[];

  @Prop({ default: false })
  allowPopoutsInRoot!: boolean;

  get thereIsOnlyOneSection() {
    return (
      this.controls.length === 1 && this.controls[0].options.type === "section"
    );
  }

  isPopoutOrModal(control: Control) {
    const type = control.options.sectionOptions?.type;
    const isNested = this.path.includes(".");

    if (!type) return false;

    return (
      ["popout", "modal"].includes(type) &&
      (this.allowPopoutsInRoot || isNested)
    );
  }

  @Prop({
    default: false
  })
  forceFirstSectionOpen!: boolean;

  @Prop({
    default: false
  })
  compact!: boolean;

  @Prop({
    default: false
  })
  nested!: boolean;

  @Prop({ default: 0 })
  depth!: number;

  @Prop()
  path!: string;

  isAccordionAtPathExpanded(controlPathWithSlug: string, index: number) {
    const isExpanded = this.context.getAccordionState(controlPathWithSlug);

    if (isExpanded !== undefined) {
      return isExpanded;
    }

    if (
      (this.forceFirstSectionOpen || this.thereIsOnlyOneSection) &&
      // only open the first one
      index === 0
    ) {
      // toggle it to "true" since it's open but not in the state yet
      this.toggleAccordion(controlPathWithSlug);
      return true;
    }

    return false;
  }

  toggleAccordion(controlPathWithSlug: string) {
    const currentState = this.context.getAccordionState(controlPathWithSlug);
    this.context.setAccordionState(controlPathWithSlug, !currentState);
  }

  @Inject("context") readonly context!: ControlsTreeContext;

  classForConditionalDisplayOfControl(control: Control, path: string) {
    if (control.options.condition || control.options.hideForElements) {
      if (
        !shouldShowControl(
          control,
          path,
          this.context,
          uiStore.currentBreakpoint.id
        )
      ) {
        return "conditional-control-display-hidden";
      }
    }

    return "conditional-control-display-visible";
  }

  get stylesPresent() {
    return (path: string, control: Control) =>
      valuePresent(path, control, this.context);
  }

  clearStyles(path: string, control: Control) {
    clear(path, control, this.context);
  }
}
